import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Paragraph,
} from '@/components/atoms';
import { formatCurrency } from '@/lib/helpers/format-currency';
import {
  REFERRAL_WITHDRAW_MINIMUM,
  REFERRAL_WITHDRAW_NOTE,
} from '../constants';
import { Link } from 'react-router';
import { routes } from '@/lib/constants';
import { useEffect, useState } from 'react';

interface WithdrawDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBalance: number;
  bankLabel?: string;
  onConfirm: (amount: number) => void;
  isSubmitting?: boolean;
}

export const WithdrawDialog = ({
  open,
  onOpenChange,
  availableBalance,
  bankLabel,
  onConfirm,
  isSubmitting = false,
}: WithdrawDialogProps) => {
  const [amount, setAmount] = useState(availableBalance);

  useEffect(() => {
    if (open) setAmount(availableBalance);
  }, [availableBalance, open]);

  const belowMinimum = availableBalance < REFERRAL_WITHDRAW_MINIMUM;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Withdraw earnings</DialogTitle>
        </DialogHeader>

        {belowMinimum ? (
          <div className="space-y-4">
            <Paragraph>
              You’re{' '}
              {formatCurrency(REFERRAL_WITHDRAW_MINIMUM - availableBalance)}{' '}
              away from cashing out. One more referral could get you there. Or
              apply what you have toward your next subscription.
            </Paragraph>
            <DialogFooter className="gap-2 sm:justify-start">
              <Button onClick={() => onOpenChange(false)}>Share My Link</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Apply to Subscription
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <Paragraph className="text-3xl font-bold">
              {formatCurrency(availableBalance)}
            </Paragraph>
            <Input
              type="number"
              min={REFERRAL_WITHDRAW_MINIMUM}
              max={availableBalance}
              value={amount}
              onChange={(event) => setAmount(Number(event.target.value))}
            />
            <Paragraph className="text-sm text-muted-foreground">
              {bankLabel ?? 'Bank on file'} ·{' '}
              <Link
                to={`${routes.settings}?tab=bank-info`}
                className="text-primary underline-offset-4 hover:underline"
              >
                Change in Settings
              </Link>
            </Paragraph>
            <Paragraph className="text-sm text-muted-foreground">
              {REFERRAL_WITHDRAW_NOTE}
            </Paragraph>
            <DialogFooter>
              <Button
                onClick={() => onConfirm(amount)}
                isLoading={isSubmitting}
                disabled={amount < REFERRAL_WITHDRAW_MINIMUM}
              >
                Confirm withdrawal
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
